import { NavigateOptions } from 'react-router-dom';
import { PropsWithChildren } from 'react';

import { FilterContext } from './context/filter-context';
import { SearchContext } from './context/search-context';
import { useFilterDisplayCache } from './context/filter-display-cache-context';

export interface FilterProps extends PropsWithChildren {
    searchParams: URLSearchParams;
    defaultSearchParams?: URLSearchParams;
    onUpdateSearchParams: (searchParams: URLSearchParams, navigateOpts?: NavigateOptions) => void;
    useLegacyFilter?: boolean;
    replaceRoute?: boolean;
}

export const Filter = (props: FilterProps) => {
    const { registerFilterMap, getDisplayValue, getFromCache, updateCache } = useFilterDisplayCache();

    const handleReset = (): void => {
        const filter: URLSearchParams = new URLSearchParams(props.defaultSearchParams);

        const persistentFilterKeys = ['limit'];
        for (const [key, value] of props.searchParams.entries()) {
            if (persistentFilterKeys.includes(key)) {
                filter.set(key, value);
            }
        }

        updateSearchParams(filter);
    };

    const getFilter = (): URLSearchParams => {
        if (!props.useLegacyFilter) {
            return props.searchParams;
        }

        // Legacy support
        const mappedLegacyFilter = new URLSearchParams();
        Array.from(props.searchParams.entries()).forEach(([key, value]) => {
            if (key.startsWith('filter[')) {
                // "filter[status]" and "filter[status][]" name the same filter;
                // without dropping the brackets of the list form the chip would
                // be labelled "status][".
                key = key.substring(7, key.indexOf(']', 7));
            }
            mappedLegacyFilter.append(key, value);
        });
        return mappedLegacyFilter;
    };

    const handleUpdateFilter = (searchParams: URLSearchParams): void => {
        props.searchParams.set('page', '1');
        if (!props.useLegacyFilter) {
            updateSearchParams(searchParams);
            return;
        }

        // Legacy support
        const ignoredFilterKeys = ['sort', 'order', 'search', 'limit', 'page', 'pageSize'];
        const mappedLegacyFilter = new URLSearchParams();
        Array.from(searchParams.entries()).forEach(([key, value]) => {
            if (ignoredFilterKeys.includes(key)) {
                mappedLegacyFilter.set(key, value);
            } else {
                mappedLegacyFilter.append(`filter[${key}]`, value);
            }
        });
        updateSearchParams(mappedLegacyFilter);
    };

    const handleUpdateSearch = (search: string): void => {
        props.searchParams.set('page', '1');
        props.searchParams.delete('search');
        if (search) {
            props.searchParams.set('search', search);
        }
        updateSearchParams(props.searchParams);
    };

    const updateSearchParams = (searchParams: URLSearchParams): void => {
        props.onUpdateSearchParams(searchParams, { replace: props.replaceRoute });
    };

    return (
        <FilterContext.Provider
            value={{
                filter: getFilter(),
                updateFilter: handleUpdateFilter,
                reset: handleReset,

                registerFilterMap,
                getDisplayValue,
                getFromCache,
                updateCache,
            }}
        >
            <SearchContext.Provider
                value={{
                    search: props.searchParams.get('search') ?? '',
                    onUpdateSearch: handleUpdateSearch,
                }}
            >
                {props.children}
            </SearchContext.Provider>
        </FilterContext.Provider>
    );
};
